"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Loader2, MessagesSquare, Send } from "lucide-react";

import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useAskClarification, useContestClarifications } from "../hooks";
import { contestErrorKey, formatDateTime } from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import { CONTEST_CLARIFICATION_QUESTION_MAX } from "../types";

/**
 * Clarifications - `docs/29` §6.2.
 *
 * The opposite of an announcement and deliberately so: an answer is addressed
 * to somebody, so both reading and asking need an active participant. The list
 * the server returns is already narrowed to what this reader may see (their
 * own, their team's, and everything answered to all), so nothing here filters
 * it again.
 */
export function ContestClarificationsPanel({
  slug,
  locale,
  canAsk,
  problemLetters,
}: {
  slug: string;
  locale: string;
  /** An active participant while the contest is open - the server's answer, not ours. */
  canAsk: boolean;
  problemLetters: string[];
}) {
  const t = useTranslations("contest.clarifications");
  const zone = useContestDisplayZone();
  const tErrors = useTranslations("contest.errors");

  const query = useContestClarifications(slug);
  const ask = useAskClarification(slug);

  const [letter, setLetter] = useState("");
  const [question, setQuestion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = question.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await ask.mutateAsync({ ...(letter ? { letter } : {}), question: trimmed });
      setQuestion("");
      setLetter("");
    } catch (err) {
      const key = contestErrorKey(err);
      setError(key ? tErrors(key) : err instanceof ApiError ? err.message : t("askFailed"));
    }
  };

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-4">
      {canAsk ? (
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
            {t("askTitle")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={letter}
              onChange={(event) => setLetter(event.target.value)}
              className="h-9 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-sm text-neutral-800"
            >
              <option value="">{t("aboutContest")}</option>
              {problemLetters.map((value) => (
                <option key={value} value={value}>
                  {t("aboutProblem", { letter: value })}
                </option>
              ))}
            </select>
          </div>
          <textarea
            value={question}
            onChange={(event) =>
              setQuestion(event.target.value.slice(0, CONTEST_CLARIFICATION_QUESTION_MAX))
            }
            rows={3}
            placeholder={t("questionPlaceholder")}
            className="mt-2 w-full rounded-xl border border-[rgb(var(--border))] bg-white p-3 text-sm text-neutral-800 outline-none focus:border-[#E94D1D]"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
              {question.length} / {CONTEST_CLARIFICATION_QUESTION_MAX}
            </span>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={ask.isPending || !question.trim()}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-[#E94D1D] px-3.5 text-sm font-bold text-white transition-colors hover:bg-[#d44119] disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400"
            >
              {ask.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {t("ask")}
            </button>
          </div>
          {error ? (
            <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {query.isPending ? (
        <Skeleton className="h-32 w-full rounded-2xl" />
      ) : query.isError ? (
        <p className="rounded-2xl border border-[rgb(var(--border))] bg-white p-6 text-center text-sm text-[rgb(var(--muted-foreground))]">
          {t("unavailable")}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-white p-8 text-center shadow-card">
          <MessagesSquare className="mx-auto h-6 w-6 text-neutral-300" />
          <p className="mt-2 text-sm text-[rgb(var(--muted-foreground))]">{t("empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item._id}
              className="rounded-2xl border border-[rgb(var(--border))] bg-white p-4 shadow-card"
            >
              <div className="flex flex-wrap items-center gap-2">
                {item.letter ? (
                  <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-bold text-neutral-700">
                    {item.letter}
                  </span>
                ) : null}
                <span
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                    item.status === "answered"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700",
                  )}
                >
                  {t(`status_${item.status}`)}
                </span>
                {item.mine ? (
                  <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {t("mine")}
                  </span>
                ) : null}
                <span className="ml-auto text-[11px] text-[rgb(var(--muted-foreground))]">
                  {formatDateTime(item.createdAt, locale, zone)}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-800">{item.question}</p>
              {item.answer ? (
                <div className="mt-3 rounded-xl bg-neutral-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
                    {t("answer")}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-800">{item.answer}</p>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
